import { logger } from 'Services/logger/logger';
import { BID_TYPES, BIDDING_STATES } from 'Utilities/biddingState';
import { SLUGS } from 'Utilities/vehicleState';

import {
	sendBrazeOfferBINEvent,
	sendBrazeProxyBidEvent,
	sendBrazePurchaseBINEvent,
	sendBrazeViewsAuctionEvent,
	sendBrazeViewsBINEvent,
} from './brazeEvents';

interface SendBaseEnquiryBrazeEventParams {
	enquiryId: number;
}

interface SendVehicleViewedBrazeEventParams extends SendBaseEnquiryBrazeEventParams {
	slug: string;
}

interface SendCreateBidBrazeEventParams extends SendBaseEnquiryBrazeEventParams {
	bidState: string;
	bidType: string;
}

export const sendBrazeCustomEvent = async (eventName: string, eventPayload?: object): Promise<void> => {
	try {
		const { logCustomEvent } = await import('Services/braze/brazeExports');

		logCustomEvent(eventName, eventPayload);
	} catch (e) {
		const error = e instanceof Error ? e : new Error('Failed to send Braze custom event');
		logger.warn({ error, message: error.message, scope: 'sendBrazeCustomEvent' });
	}
};

export const sendVehicleViewedBrazeEvent = async ({
	enquiryId,
	slug,
}: SendVehicleViewedBrazeEventParams): Promise<void> => {
	switch (slug) {
		case SLUGS.forSale:
			await sendBrazeViewsBINEvent(enquiryId);
			break;
		case SLUGS.saleActive:
			await sendBrazeViewsAuctionEvent(enquiryId);
			break;
		default:
			break;
	}
};

// Bid here refers to proxy (auction), offer, and BIN purchase (all constitute a bid)
export const sendCreateBidBrazeEvent = async ({
	bidState,
	bidType,
	enquiryId,
}: SendCreateBidBrazeEventParams): Promise<void> => {
	const isProxyBid = bidType === BID_TYPES.PROXY;
	const isBINOfferOrPurchase = bidType === BID_TYPES.OFFER;

	const isBINOffer = isBINOfferOrPurchase && bidState === BIDDING_STATES.PENDING;
	const isBINPurchase = isBINOfferOrPurchase && bidState === BIDDING_STATES.ACCEPTED;

	if (isProxyBid) {
		await sendBrazeProxyBidEvent(enquiryId);
		return;
	}

	if (isBINOffer) {
		await sendBrazeOfferBINEvent(enquiryId);
		return;
	}

	if (isBINPurchase) {
		await sendBrazePurchaseBINEvent(enquiryId);
	}
};
